// Guided CSV import (plan §7, D9): read the file in the browser, map its columns, dry-run on the
// server, then import. Nothing is stored until the last step.
import { useMemo, useState } from "react";
import { Badge, Banner, Button, Card, Select, Table, type BadgeTone } from "@vavr/ui";
import { api } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { FileButton } from "./common.tsx";
import { autoMap, buildRows, childKey, parseCsv, TARGETS, type BuiltRow, type Mapping, type TargetKey } from "./csv.ts";

interface Report { created: number; updated: number; duplicates: number[]; errors: { row: number; error: string }[]; adults: number }
const MAX_ROWS = 5000;
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

export function Import() {
  const { base } = useConsole();
  const existing = useGet<{ children: { first: string; last: string; dob: string | null }[] }>(`${base}/children`, 30_000);
  const [file, setFile] = useState<{ name: string; records: string[][] } | null>(null);
  const [mapping, setMapping] = useState<Mapping>([]);
  const [dry, setDry] = useState<Report | null>(null);
  const [done, setDone] = useState<Report | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const act = useAction();

  const headers = file?.records[0] ?? [];
  const data = useMemo(() => file?.records.slice(1) ?? [], [file]);
  const built = useMemo(() => buildRows(data, mapping), [data, mapping]);
  // Rows the browser already knows are wrong are not sent: fixing the file is safer than guessing.
  const sendable = useMemo(() => built.filter((b) => b.problems.length === 0), [built]);
  const known = useMemo(() => new Set((existing.data?.children ?? []).map((c) => childKey(c.first, c.last, c.dob))), [existing.data]);

  async function load(f: File) {
    setReadError(null); setDry(null); setDone(null);
    const text = await f.text();
    const records = parseCsv(text);
    if (records.length < 2) { setReadError("That file has no rows under the header."); return; }
    if (records.length - 1 > MAX_ROWS) { setReadError(`That file has ${records.length - 1} rows. Import at most ${MAX_ROWS} at a time.`); return; }
    setFile({ name: f.name, records });
    setMapping(autoMap(records[0]!));
  }
  const setTarget = (col: number, t: TargetKey | null) => {
    setDry(null);
    setMapping((m) => m.map((x, i) => (i === col ? t : x === t && t ? null : x)));
  };
  const send = (dryRun: boolean) => act.run(async () => {
    const r = await api<Report>("POST", `${base}/import`, { rows: sendable.map((b) => b.row), dry_run: dryRun });
    if (dryRun) setDry(r); else { setDone(r); setDry(null); }
  }, dryRun ? {} : { success: "Roster imported.", invalidate: [`${base}/children`, `${base}/adults`, `${base}/rooms`, `${base}/board`] });

  const mapped = new Set(mapping.filter(Boolean));
  const hasChildName = (mapped.has("child_first") && mapped.has("child_last")) || mapped.has("child_name");
  const status = (b: BuiltRow, i: number): { label: string; tone: BadgeTone; detail: string } => {
    const err = dry?.errors.find((e) => e.row === i);
    if (err) { const t = TARGETS.find((x) => x.key === err.error); return { label: "Error", tone: "danger", detail: t ? `${t.label} is missing or too long` : err.error }; }
    if (dry?.duplicates.includes(i)) return { label: "Duplicate in file", tone: "warning", detail: "The same child appears earlier in this file; this row is skipped." };
    if (b.problems.length) return { label: "Not imported", tone: "warning", detail: `${b.problems.join("; ")}. Fix it in your file and choose the file again.` };
    const update = known.has(childKey(b.row.child_first ?? "", b.row.child_last ?? "", b.row.child_dob));
    return { label: update ? "Update" : "New", tone: update ? "info" : "success", detail: update ? "Matches a child already on file (name and date of birth)" : "" };
  };

  return (
    <div className="pmu-stack">
      <Card title="1. Choose your file" description="Export children and parents from Procare, Brightwheel, a spreadsheet or your school system as CSV. The file is read here in your browser; nothing is sent until you check it.">
        <div className="pmu-row">
          <FileButton label={file ? "Choose another file" : "Choose a CSV file"} accept=".csv,text/csv,text/plain" onFile={(f) => void load(f)} />
          {file && <span>{file.name}: {data.length} rows, {headers.length} columns</span>}
        </div>
        {readError && <Banner tone="danger" title={readError} />}
      </Card>

      {file && (
        <Card title="2. Match the columns" description="We guessed from the headers. Fix anything that is wrong, and skip columns you do not need. Each field can be used once." flush>
          <Table
            caption="Column mapping" hideCaption rowKey={(r) => String(r.col)} rows={headers.map((h, col) => ({ h, col }))}
            columns={[
              { key: "h", header: "Your column", rowHeader: true, render: (r) => r.h || <span className="pmu-muted">(no header)</span> },
              { key: "ex", header: "First values", render: (r) => <span className="pmu-small pmu-muted">{data.slice(0, 3).map((x) => x[r.col]).filter(Boolean).join(" · ")}</span> },
              {
                key: "to", header: "Goes into", width: "18rem", render: (r) => (
                  <Select label={`Field for ${r.h || `column ${r.col + 1}`}`} className="pmu-c-inline-select" value={mapping[r.col] ?? "skip"}
                    onValueChange={(v) => setTarget(r.col, !v || v === "skip" ? null : (v as TargetKey))}
                    options={[{ value: "skip", label: "Skip this column" }, ...TARGETS.map((t) => ({ value: t.key, label: t.label }))]} />
                ),
              },
            ]}
          />
          {!hasChildName && <div className="pmu-c-pad"><Banner tone="warning" title="Match the child's first and last name (or full name) to continue." /></div>}
        </Card>
      )}

      {file && hasChildName && (
        <Card title="3. Check, then import" description="The check runs every row through the same rules as the import, without saving anything."
          actions={<>
            <Button variant={dry ? "secondary" : "primary"} loading={act.busy && !dry} onClick={() => send(true)}>Check {sendable.length} {sendable.length === 1 ? "row" : "rows"}</Button>
            <Button disabled={!dry || act.busy} loading={act.busy && !!dry} onClick={() => send(false)}>Import</Button>
          </>} flush>
          {act.error && <div className="pmu-c-pad"><Banner tone="danger" title={act.error} /></div>}
          {dry && (
            <div className="pmu-c-pad">
              <Banner tone={dry.errors.length || built.length > sendable.length ? "warning" : "success"} title={`${dry.created} new, ${dry.updated} updated, ${plural(dry.duplicates.length, "duplicate")}, ${dry.errors.length} with errors${built.length > sendable.length ? `, ${built.length - sendable.length} not imported` : ""}`}>
                {dry.errors.length || built.length > sendable.length ? "Rows with problems are skipped. Fix them in your file and import again, or import the rest now." : "Everything checks out."}
              </Banner>
            </div>
          )}
          {done && <div className="pmu-c-pad"><Banner tone="success" title={`Imported: ${done.created} new children, ${done.updated} updated, ${done.adults} adults linked.`}>{done.errors.length ? `${done.errors.length} rows were skipped with errors.` : ""}</Banner></div>}
          <Table
            caption="Rows to import" hideCaption rowKey={(r) => String(r.b.source)} rows={built.map((b) => ({ b, s: status(b, sendable.indexOf(b)) }))} maxHeight="60vh"
            columns={[
              { key: "n", header: "Row", width: "4rem", align: "end", render: (r) => r.b.source + 2 },
              { key: "c", header: "Child", rowHeader: true, render: (r) => [r.b.row.child_first, r.b.row.child_last].filter(Boolean).join(" ") || <span className="pmu-muted">Missing</span> },
              { key: "d", header: "Date of birth", render: (r) => r.b.row.child_dob ?? "" },
              { key: "r", header: "Room", render: (r) => r.b.row.room ?? "" },
              { key: "a", header: "Parents", render: (r) => [1, 2, 3].map((n) => [r.b.row[`adult${n}_first`], r.b.row[`adult${n}_last`]].filter(Boolean).join(" ")).filter(Boolean).join(", ") },
              { key: "s", header: "Result", render: (r) => <span className="pmu-stack pmu-c-tight"><Badge tone={r.s.tone}>{r.s.label}</Badge>{r.s.detail && <span className="pmu-small">{r.s.detail}</span>}</span> },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
